import { getResourceTypeDetails, ResourceType, ResourceTypeDetails, ResourceVariant } from 'src/utils/resource-types'
import { Arn } from 'src/utils/types'
import {
  ResourceStatus,
  ViewableApplicationResource,
} from '../account/components/app-resources-view/app-resources-view.component'
import {
  AppResourceStatusChangeDto,
  ApplicationResourceDto,
  ApplicationResourceStatusDto,
  ApplicationSourceResourceDto,
  ApplicationType,
  AwsTargetResourceDto,
  ResourcePropertyDto,
  SyncPairResourceStateDto,
  SyncPairResourceStatusDto,
} from '../services/dtos'
import { computeResourceDisplayName } from './utils'
import { RaisedIssue } from '../account/services/issue'

// Substatus emitted by the processRecentlyCompleted PENDING bridge in _computeStatus.
// The pill label resolver discriminates on this exact string to render "Pending"
// instead of "Queued" — keep the writer and the reader pointing at the same constant.
export const CONFIRMING_STATUS_MESSAGE = 'Confirming status...'

// Substatus emitted when a resource was mid-restore but the job ended before finalizing it.
// The pill label resolver discriminates on this exact string to render "Finalizing".
export const FINALIZING_STATUS_MESSAGE = 'Finalizing'

/**
 * Compute substatus text based on resource status, progress, and message.
 */
export function computeResourceSubstatus(
  status: ResourceStatus,
  progress: number,
  message: string | null
): string | undefined {
  switch (status) {
    case ResourceStatus.PENDING:
      return message || 'Waiting in queue...'

    case ResourceStatus.PROCESSING: {
      if (progress > 0 && progress < 1) {
        return `${Math.round(progress * 100)}%`
      }
      return message || undefined
    }

    case ResourceStatus.UNSATISFIED_DEPENDENCY:
      return message || 'Waiting for dependency'

    case ResourceStatus.FAILED:
      return message || undefined

    default:
      return undefined
  }
}

export function translateSyncPairResourceStateDto(state: SyncPairResourceStateDto) {
  switch (state) {
    case SyncPairResourceStateDto.INIT:
      return ResourceStatus.PENDING
    case SyncPairResourceStateDto.PROCESSING:
      return ResourceStatus.PROCESSING
    case SyncPairResourceStateDto.UNSATISFIED_DEPENDENCY:
      return ResourceStatus.UNSATISFIED_DEPENDENCY
    case SyncPairResourceStateDto.FAILURE:
      return ResourceStatus.FAILED
    case SyncPairResourceStateDto.SUCCESS:
      return ResourceStatus.SUCCEEDED
    case SyncPairResourceStateDto.QUARANTINED:
      return ResourceStatus.QUARANTINED
    case SyncPairResourceStateDto.SCAN_CLEAN:
      return ResourceStatus.SCAN_CLEAN
    case SyncPairResourceStateDto.SCAN_INFECTED:
      return ResourceStatus.SCAN_INFECTED
    case SyncPairResourceStateDto.SCAN_FAILED:
      return ResourceStatus.SCAN_FAILED
  }
  return ResourceStatus.NO_STATUS
}

export class FailbackResource implements ViewableApplicationResource {
  resourceType: ResourceType
  resourceTypeDetails: ResourceTypeDetails
  displayResourceType: string
  resourceArn: Arn
  targetResourceArn?: Arn
  issues: Set<RaisedIssue> = new Set()
  message: string = null
  progress = 0
  resourceProperties: Array<ResourcePropertyDto> = []
  resourceVariant: ResourceVariant = null
  sourceResourceName: string = null
  sourceResourceGroup?: string = null
  targetResourceName: string
  targetResourceGroup?: string
  status: ResourceStatus = ResourceStatus.NO_STATUS
  embeddedStatusTimestamp?: Date
  lastStatusTimestamp?: Date
  hasIssues: boolean = false

  // Derived from status/progress/message so it stays consistent if any of those mutate
  // post-construction (e.g., via incremental status-change polling).
  get substatus(): string | undefined {
    return computeResourceSubstatus(this.status, this.progress, this.message)
  }

  constructor(sourceName: string, targetResource: AwsTargetResourceDto, status: SyncPairResourceStatusDto) {
    this.resourceType = targetResource.type
    this.resourceArn = targetResource.arn
    this.targetResourceArn = targetResource.arn
    this.targetResourceName = computeResourceDisplayName(
      targetResource.id,
      targetResource.name,
      targetResource.displayName
    )
    this.sourceResourceName = sourceName
    this.targetResourceGroup = targetResource.resourceGroup
    this.resourceProperties = []
    this.resourceVariant = targetResource.variant
    this.resourceTypeDetails = getResourceTypeDetails({
      resourceType: this.resourceType,
      resourceVariant: this.resourceVariant,
    })
    this.progress = status.progress
    this.message = status.message
    this.status = translateSyncPairResourceStateDto(status.state)
    this.displayResourceType = this.resourceTypeDetails.displayName
    this.lastStatusTimestamp = status.timestamp
  }
}

function azurePortalUri(resourceId: string): string | undefined {
  if (!resourceId) return undefined
  return `https://portal.azure.com/#@/resource${resourceId}`
}

/**
 * ApplicationResource combines a source resource and a target resource into a single object.
 */
export class ApplicationResource implements ViewableApplicationResource {
  status: ResourceStatus
  progress: number
  message: string
  sourceRes: ApplicationSourceResourceDto
  sourceResName?: string
  targetResName = ''
  private _statusTimestamp?: Date
  resourceTypeDetails: ResourceTypeDetails

  sourceResourcePortalUri?: string
  targetResourcePortalUri?: string
  resourcePortalName?: string

  constructor(
    private appResource: ApplicationResourceDto,
    private targetRes: AwsTargetResourceDto | null,
    private latestStatus: ApplicationResourceStatusDto | AppResourceStatusChangeDto | null,
    public issues: Set<RaisedIssue>,
    private currentlyProcessing: boolean,
    private processingHorizon: Date,
    applicationType: ApplicationType = ApplicationType.AWS,
    private scope: 'backup' | 'restore' | 'failback' = 'restore',
    private processRecentlyCompleted: boolean = false
  ) {
    this.sourceRes = appResource.source
    this.resourceTypeDetails = getResourceTypeDetails({
      resourceType: appResource.type,
      resourceVariant: appResource.variant,
    })
    ;[this.status, this.progress, this.message, this._statusTimestamp] = this._computeStatus()

    // Pilot-light resources: backed up but not deployed in standby. Treat as SUCCESS
    // in restore/failback scope — the resource is healthy, it just hasn't been and
    // won't be materialized until an active recovery test or failover runs. The
    // Recovery Resource cell naturally falls back to empty because targetRes is null.
    // The substatus getter surfaces the pilot-light hint via isPilotLight().
    if (this.isPilotLight()) {
      this.status = ResourceStatus.SUCCEEDED
      this.progress = 1
    }

    if (applicationType === ApplicationType.AZURE) {
      // K8s resources under an AKS cluster don't have working Azure portal URLs, so suppress the link.
      if (appResource.type !== ResourceType.k8sResource) {
        this.sourceResourcePortalUri = azurePortalUri(this.sourceRes?.id)
        this.targetResourcePortalUri = azurePortalUri(this.targetRes?.id)
      }
      this.resourcePortalName = 'Azure Portal'
    }
  }

  private _computeStatus(): [ResourceStatus, number, string, Date | null] {
    let latestStatus = this.latestStatus || this.appResource.status

    if (this.currentlyProcessing && latestStatus && latestStatus.timestamp > this.processingHorizon) {
      // We are processing and the latest status came in after we started processing
      const status = translateSyncPairResourceStateDto(latestStatus.state)
      return [status, latestStatus.progress, latestStatus.message, latestStatus.timestamp]
    } else if (this.currentlyProcessing) {
      // We are processing and latest status is stale (from previous job).  Show pending status
      return [ResourceStatus.PENDING, 0, null, null]
    } else if (latestStatus && latestStatus.timestamp < this.processingHorizon) {
      // The latest status was produced prior to the most recently completed job.  Show no status.
      return [ResourceStatus.NO_STATUS, 0, null, null]
    } else if (latestStatus) {
      // Not processing and latest status is up-to-date. Just show the most recent status we have from previously
      // completed job
      let status = translateSyncPairResourceStateDto(latestStatus.state)

      // Not restoring, but the last recorded state never finalized. PENDING = never reached
      // (a dependee failed first) → Not Processed; PROCESSING = started but the job ended first →
      // route through the PENDING bridge so it shows "Finalizing", not a stuck "Restoring".
      if (status == ResourceStatus.PENDING) {
        status = ResourceStatus.NOT_PROCESSED
      } else if (status == ResourceStatus.PROCESSING) {
        return [ResourceStatus.PENDING, 0, FINALIZING_STATUS_MESSAGE, null]
      }

      return [status, latestStatus.progress, latestStatus.message, latestStatus.timestamp]
    } else if (this.processRecentlyCompleted) {
      // Not processing, job recently completed successfully (~30s window), but the
      // per-resource record hasn't propagated yet (S3 write latency / change-log
      // polling lag). Render PENDING so the pill conveys activity instead of a
      // misleading "Not Started" — we still don't claim SUCCESS, so a row that
      // actually failed won't be masked once its FAILED record arrives.
      return [ResourceStatus.PENDING, 0, CONFIRMING_STATUS_MESSAGE, null]
    } else {
      // Not processing and no per-resource status available. Don't infer SUCCESS from
      // job-level outcome alone — individual resources can fail without flipping the
      // job's lastFailure timestamp (e.g. MQ broker provisioning that AWS accepts but
      // later marks CREATION_FAILED). Showing a green pill without a per-resource
      // record risks claiming success for resources that actually failed.
      return [ResourceStatus.NO_STATUS, 0, null, null]
    }
  }

  // Pilot-light: in recovery/failback scope, the resource is present in the current recovery
  // point (source exists) but has no target materialized in standby and no restore status
  // recorded. Healthy standby state — treat as SUCCESS and let the empty target name
  // naturally leave the Recovery Resource cell blank.
  private isPilotLight(): boolean {
    return (
      this.scope !== 'backup' && !this.currentlyProcessing && !this.latestStatus && !!this.sourceRes && !this.targetRes
    )
  }

  // Orphaned target: resource exists in the recovery environment but isn't in the current
  // recovery point (source is null). Usually a leftover from a prior recovery test or a
  // config change. Keep the computed status but tag it with a substatus that explains why
  // it's showing up with no Primary Resource.
  private isOrphanedTarget(): boolean {
    return this.scope !== 'backup' && this.status === ResourceStatus.NO_STATUS && !this.sourceRes && !!this.targetRes
  }

  // Derived from status/progress/message so it stays consistent if any of those mutate
  // post-construction (e.g., via incremental status-change polling). Pilot-light and
  // orphaned-target overrides take priority — those flags depend on construction-time
  // inputs (sourceRes/targetRes/scope) that don't change after the object is built.
  get substatus(): string | undefined {
    if (this.isPilotLight()) return 'Pilot-light resource — created on demand during recovery'
    if (this.isOrphanedTarget()) return 'Not in the current recovery point'
    return computeResourceSubstatus(this.status, this.progress, this.message)
  }

  get sourceResourceName() {
    /**
     * Combine the resourceName and the resourceId into a single field to show in the UI
     * if the resourceName exists and it isn't the same as the ID.
     */
    if (!this.sourceResName) {
      if (!this.sourceRes) {
        // If no source resource was returned, it means it was deleted from the source or we no longer have access to it.
        // In that case, we use an empty string as the display name.
        return ''
      }
      this.sourceResName = computeResourceDisplayName(
        this.sourceRes.id,
        this.sourceRes.name,
        this.sourceRes.displayName
      )
    }
    return this.sourceResName
  }

  get targetResourceName() {
    /**
     * Same as resourceDisplayName, but looks at the target resource.
     */
    if (!!this.targetRes && !this.targetResName) {
      this.targetResName = computeResourceDisplayName(
        this.targetRes.id,
        this.targetRes.name,
        this.targetRes.displayName
      )
    }
    return this.targetResName
  }

  get sourceResourceGroup() {
    return this.sourceRes?.resourceGroup
  }

  // Parent resource ARN — used to link children to expandable parent rows (e.g., K8s resources → EKS cluster)
  get parentArn(): string | null {
    return this.sourceRes?.parentArn || null
  }

  get targetResourceGroup() {
    return this.targetRes?.resourceGroup
  }

  get resourceProperties() {
    if (!!this.targetRes) {
      return this.targetRes.properties
    }
    return []
  }

  get resourceType() {
    return this.appResource.type
  }

  get resourceVariant() {
    return this.appResource.variant
  }

  get resourceArn() {
    return this.appResource.arn
  }

  // Target (recovery) ARN — available when restore target exists
  get targetResourceArn(): string | undefined {
    return this.targetRes?.arn
  }

  get resourceId() {
    return this.sourceRes?.id || this.targetRes?.id || ''
  }

  get displayResourceType() {
    return this.resourceTypeDetails.displayName
  }

  get embeddedStatusTimestamp(): Date | undefined {
    return this.appResource.status?.timestamp
  }

  get lastStatusTimestamp(): Date | undefined {
    return this._statusTimestamp
  }

  get hasIssues(): boolean {
    return this.issues.size > 0
  }
}
